import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, CheckSquare, FileText, Image } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet'
import { cn } from '@/shared/lib/utils'
import { canReachRoute } from '@/shared/components/layout/navGroups'
import { NotificationPanel } from '@/shared/components/layout/NotificationPanel'
import { PHONE_BAR_OFFSET } from '@/shared/components/layout/PhoneBar'

/**
 * The two bottom sheets the phone bar opens (RB-40, D84).
 *
 * Both stop ABOVE the bar and leave it visible and tappable (IN-322, Andras: *"opening the
 * notifications hides the bottom bar. it should keep it visible like all the other elements"*) —
 * the same shape as the full-screen menu. That rules out a modal Radix sheet, whose scrim covers
 * the bar and whose pointer-event lock makes it dead: each sheet is non-modal, draws its own
 * scrim that ends at the bar's top edge, and ignores a pointer-down on the bar control that
 * opened it, so a re-tap of Notifications or "+" toggles the sheet closed through the bar's own
 * handler instead of closing-then-reopening it. Any other tap outside, the X, Escape and a swipe
 * down still dismiss.
 */

/** Which bar control a sheet belongs to; the bar marks its buttons with the same values. */
export type PhoneBarControl = 'notifications' | 'create'

function PhoneBarSheet({ open, onOpenChange, control, testId, className, children }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  control: PhoneBarControl
  testId: string
  className?: string
  children: ReactNode
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      {open && (
        <div
          aria-hidden
          data-testid={`${testId}-scrim`}
          className="fixed inset-x-0 top-0 z-40 bg-black/50 md:hidden"
          style={{ bottom: PHONE_BAR_OFFSET }}
          onClick={() => onOpenChange(false)}
        />
      )}
      <SheetContent
        side="bottom"
        className={cn('rounded-t-xl p-0', className)}
        style={{ bottom: PHONE_BAR_OFFSET }}
        data-testid={testId}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement | null
          if (target?.closest?.(`[data-phone-bar-control="${control}"]`)) e.preventDefault()
        }}
      >
        {children}
      </SheetContent>
    </Sheet>
  )
}

type CreateRow = { key: string; label: string; icon: typeof Camera; reachable: boolean; run: () => void }

/**
 * "+": the four things Andras wants created from a phone — New Task, New Shoot, New Gallery,
 * New Offer (D84; "Create Inquiry doesn't really make sense to me"). Each row is offered only to
 * a member whose route table admits its page (`canReachRoute`), so the sets are Owner/Admin 4,
 * Crew 3 (no offers), Bookkeeper 1 (tasks) — nobody is offered a create that ends in a 403.
 * New Task opens the shell's own dialog in place, as the floating button did; the others open
 * their page's create form through its `?new=1` deep link.
 */
export function PhoneCreateSheet({ open, onOpenChange, roles, onNewTask, navigate }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  roles: readonly string[]
  onNewTask: () => void
  navigate: (to: string) => void
}) {
  const { t } = useTranslation()
  const rows: CreateRow[] = [
    { key: 'task', label: t('layout:taskFab.newTask'), icon: CheckSquare, reachable: canReachRoute('/tasks', roles) || canReachRoute('/my-tasks', roles), run: onNewTask },
    { key: 'shoot', label: t('layout:createSheet.newShoot'), icon: Camera, reachable: canReachRoute('/shoots', roles), run: () => navigate('/shoots?new=1') },
    { key: 'gallery', label: t('lists:galleries.newButton'), icon: Image, reachable: canReachRoute('/galleries', roles), run: () => navigate('/galleries?new=1') },
    { key: 'offer', label: t('lists:offers.newButton'), icon: FileText, reachable: canReachRoute('/offers', roles), run: () => navigate('/offers?new=1') },
  ]
  return (
    <PhoneBarSheet open={open} onOpenChange={onOpenChange} control="create" testId="create-sheet">
      <SheetHeader className="border-b px-4 pr-12 py-3 text-left space-y-0">
        <SheetTitle className="text-base">{t('common:button.create')}</SheetTitle>
      </SheetHeader>
      <div className="flex flex-col p-2">
        {rows.filter(r => r.reachable).map(({ key, label, icon: Icon, run }) => (
          <button
            key={key}
            type="button"
            onClick={() => { onOpenChange(false); run() }}
            className="flex min-h-12 items-center gap-3 rounded-md px-3 text-left text-base active:bg-accent [-webkit-tap-highlight-color:transparent]"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted"><Icon size={18} /></span>
            {label}
          </button>
        ))}
      </div>
    </PhoneBarSheet>
  )
}

/**
 * Notifications: the same NotificationPanel as the desktop bell (list, announcements, mark all
 * read, per-row read, delete all), in a sheet at the right thumb instead of a dropdown under a
 * top bar that no longer exists.
 */
export function PhoneNotificationsSheet({ open, onOpenChange, unreadCount, isOwnerOrAdmin }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  unreadCount: number
  isOwnerOrAdmin: boolean
}) {
  const { t } = useTranslation()
  return (
    <PhoneBarSheet open={open} onOpenChange={onOpenChange} control="notifications" testId="notifications-sheet" className="flex max-h-[80dvh] flex-col">
      {/* The panel's own header names it on screen; Radix needs a title for the dialog. */}
      <SheetTitle className="sr-only">{t('layout:notifications.title')}</SheetTitle>
      <div className="flex min-h-0 flex-1 flex-col">
        <NotificationPanel open={open} unreadCount={unreadCount} isOwnerOrAdmin={isOwnerOrAdmin} onNavigate={() => onOpenChange(false)} />
      </div>
    </PhoneBarSheet>
  )
}
